export default class FestzeltOSSeatPlanDrawing {
    constructor(canvas, drawInfo, events, debug, enableStatusDifferentSelect) {
        this.canvas = canvas;
        this.drawInfo = drawInfo;
        this.events = events;
        this.enableStatusDifferentSelect = !!enableStatusDifferentSelect
        this.debug = debug || false;
        this._internal = {
            canvas: canvas,
            drawInfo: drawInfo,
            context: null,
            drawObjects: [],
            mouse: {
                x: 0,
                y: 0
            },
            events: events || [],
            lastHovered: null,
            curRotation: 0,
            selectedStatus: '',
            selectedIds: []
        };

        this._defineInternalEvents();
        this._defineInternalFilters();
        this._initDrawing();
    }

    // Intializers
    _initDrawing() {
        this.debugLog('started canvas creation', this.canvas, this.drawInfo);
        this._initCanvasProperties();
        this._redrawWholeGraphic();
        this._initEvents();
    }

    _initCanvasProperties() {
        this.debugLog('creating canvas context and setting base properties');
        this._internal.context = this.canvas.getContext('2d');
        this.debugLog(this._internal.context);
    }

    _initEvents() {
        this._internal.canvas.addEventListener('mousemove', this._events.mousemove);
        this._internal.canvas.addEventListener('click', this._events.clickDecision);
        this._internal.canvas.addEventListener('dblclick', this._events.nothing);
    }

    // Debug
    debugLog(dataToLog) {
        if (this.debug) {
            console.log.apply(this, arguments);
        }
    }

    _redrawWholeGraphic() {
        this._setCursor('default');
        this._internal.context.beginPath();
        this._internal.context.fillStyle = this._internal.drawInfo.background;
        this._internal.context.fillRect(0, 0, this._internal.canvas.width, this._internal.canvas.height);
        this._internal.context.closePath();

        this._internal.drawObjects = this._redrawObjectsList(this._internal.drawInfo.objects);
    }

    _redrawObjectsList(drawObjectsList) {
        let returnObjects = [],
            currentHoverElement = this._getHoveredDrawElement();

        for (let i = 0; i < drawObjectsList.length; i++) {
            let currentDrawObject = drawObjectsList[i],
                hoverState = (
                    currentHoverElement
                    && (
                        (currentDrawObject.id === currentHoverElement.id)
                        || (
                            currentDrawObject.hoverWithIds
                            && currentDrawObject.hoverWithIds.length > 0
                            && currentDrawObject.hoverWithIds.indexOf(currentHoverElement.id) !== -1
                        )
                    )
                ),
                fillStyle = this._getFillStyleForDrawObject(currentDrawObject, hoverState),
                sizes;

            if (hoverState && currentHoverElement.clickable) {
                this._setCursor('pointer');
            }
            this._internal.context.beginPath();
            this._internal.context.fillStyle = fillStyle;
            switch (currentDrawObject.type) {
                case 'rectangle':
                default:
                    sizes = this._generateRectangleSizes(currentDrawObject);
                    this._fillRectangle(currentDrawObject, sizes, hoverState)
                    this._checkDrawObjectTriangles(currentDrawObject, sizes, hoverState);
                    returnObjects.push({
                        type: 'rectangle',
                        sizes: sizes
                    });

                    this._drawTextForDrawObject(currentDrawObject, sizes);
                    break;
            }

            this._internal.context.closePath();

            if (currentDrawObject.border && currentDrawObject.border.color && currentDrawObject.border.width) {
                this._drawObjectBorders(currentDrawObject, sizes);
            }

            if (currentDrawObject.objects && currentDrawObject.objects.length > 0) {
                this._redrawObjectsList(currentDrawObject.objects);
            }
        }

        return returnObjects;
    }

    _drawTextForDrawObject(drawObject, drawObjectSizes) {
        if (typeof drawObject.labels !== 'undefined') {
            this._internal.context.translate(drawObjectSizes.pos_x, drawObjectSizes.pos_y);
            for (let labelOrientation in drawObject.labels) {
                let curLabelSet = drawObject.labels[labelOrientation];
                for (let labelPosition in curLabelSet) {
                    let curLabelObject = curLabelSet[labelPosition],
                        maxFontWidth = (labelOrientation === 'vertical') ? this._calculateY(drawObject.size.y) : this._calculateX(drawObject.size.x),
                        styleInformation = this._calculateAndSetFontStyle(curLabelObject, drawObject, maxFontWidth),
                        position = {
                            x: 0,
                            y: 0
                        };

                    if (!curLabelObject.text) {
                        continue;
                    }

                    switch (labelPosition) {
                        case 'top':
                            position.x = (drawObjectSizes.size_x / 2) - styleInformation.width / 2;
                            position.y = styleInformation.size;
                            break;

                        case 'bottom':
                            position.x = (drawObjectSizes.size_x / 2) - styleInformation.width / 2;
                            position.y = drawObjectSizes.size_y - (styleInformation.size / 2);
                            break;

                        case 'center':
                            if (labelOrientation === 'vertical') {
                                position.x = -(drawObjectSizes.size_y + styleInformation.width) / 2;
                                position.y = (drawObjectSizes.size_x / 2) + (styleInformation.size / 2);
                            } else {
                                position.x = (drawObjectSizes.size_x / 2) - styleInformation.width / 2;
                                position.y = (drawObjectSizes.size_y / 2) + (styleInformation.size / 2);
                            }
                            break;

                        case 'left':
                            position.x = -(drawObjectSizes.size_y + styleInformation.width) / 2;
                            position.y = styleInformation.size;
                            break;

                        case 'right':
                            position.x = -(drawObjectSizes.size_y + styleInformation.width) / 2;
                            position.y = drawObjectSizes.size_x - (styleInformation.size / 2);
                            break;
                    }

                    if (labelOrientation === 'vertical') {
                        this._rotateContext(-Math.PI / 2);
                        this._internal.context.fillText(curLabelObject.text, position.x, position.y);
                        this._resetRotation();
                    } else {
                        this._internal.context.fillText(curLabelObject.text, position.x, position.y);
                    }
                }
            }
            this._internal.context.translate(-drawObjectSizes.pos_x, -drawObjectSizes.pos_y);
        }
    };

    _calculateAndSetFontStyle(labelObject, drawObject, maxWidth) {
        let objectsToCheck = [
                this._internal.drawInfo.font,
                drawObject.font,
                labelObject.font
            ],
            fieldsToCheck = [
                'size',
                'family',
                'color'
            ],
            calculatedValues = {};

        for (let objectKey = 0; objectKey < objectsToCheck.length; objectKey++) {
            const curObject = objectsToCheck[objectKey];
            if (typeof curObject !== 'undefined') {
                for (let fieldKey = 0; fieldKey < fieldsToCheck.length; fieldKey++) {
                    const curFieldName = fieldsToCheck[fieldKey];
                    if (curObject[curFieldName]) {
                        calculatedValues[curFieldName] = curObject[curFieldName];
                    }
                }
            }
        }

        this._internal.context.fillStyle = calculatedValues.color;

        let fontSize = calculatedValues.size + 1,
            labelWidth;

        do {
            this._internal.context.font = --fontSize + 'px ' + calculatedValues.family;
            labelWidth = this._internal.context.measureText(labelObject.text).width;
        } while (fontSize >= 0 && labelWidth > maxWidth);

        return {
            size: fontSize,
            width: labelWidth,
            family: calculatedValues.family,
            color: calculatedValues.color
        };
    }

    _drawObjectBorders(drawObject, sizes) {
        let borderWidth = this._calculateX(drawObject.border.width);
        this._internal.context.beginPath();
        this._internal.context.lineWidth = borderWidth;
        this._internal.context.strokeStyle = drawObject.border.color;
        this._internal.context.rect(sizes.pos_x, sizes.pos_y, sizes.size_x, sizes.size_y);
        this._internal.context.stroke();
        this._internal.context.closePath();
    }

    // Helpers
    _calculateX(valueToConvert) {
        return valueToConvert * (this._internal.canvas.width / this._internal.drawInfo.grid_width);
    }

    _calculateY(valueToConvert) {
        return valueToConvert * (this._internal.canvas.height / this._internal.drawInfo.grid_height);
    }

    _setCursor(cursor) {
        this._internal.canvas.style.cursor = cursor;
    }

    _rotateContext(rotateValue) {
        this._internal.curRotation = rotateValue;
        this._internal.context.rotate(rotateValue);
    }

    _resetRotation() {
        let rotationFix = 0 - this._internal.curRotation;
        this._internal.context.rotate(rotationFix);
        this._internal.curRotation = 0;
    }

    _generateRectangleSizes(drawObject) {
        return {
            pos_x: this._calculateX(drawObject.pos.x),
            pos_y: this._calculateY(drawObject.pos.y),
            size_x: this._calculateX(drawObject.size.x),
            size_y: this._calculateY(drawObject.size.y)
        };
    }

    _fillRectangle(currentDrawObject, sizes, hover) {
        if (!currentDrawObject.subs || currentDrawObject.subs.length === 0 || currentDrawObject.subs[0].fillPercentage === 100) {
            this._internal.context.fillRect(sizes.pos_x, sizes.pos_y, sizes.size_x, sizes.size_y);
            return;
        }

        let curPercentage = 0;
        let curElementIndex = 0;

        let lastTopRight = {
            x: sizes.pos_x,
            y: sizes.pos_y
        };

        let lastBottomRight = {
            x: sizes.pos_x,
            y: sizes.pos_y + sizes.size_y
        };

        while (curPercentage < 100) {
            const curSub = currentDrawObject.subs[curElementIndex];

            const curSubPercentage = curSub
                ? curSub.fillPercentage
                : 100 - curPercentage;

            const curFillStyleDrawObject = {
                ...currentDrawObject,
                status: curSub ? curSub.status : 'unbooked'
            }

            const curWidth = curSubPercentage > 0
                ? sizes.size_x * (curSubPercentage / 100)
                : 0;

            const topLeft = lastTopRight,
                topRight = {
                    x: lastTopRight.x + curWidth,
                    y: sizes.pos_y
                },
                bottomRight = {
                    x: lastBottomRight.x + curWidth,
                    y: sizes.pos_y + sizes.size_y
                },
                bottomLeft = lastBottomRight;

            curPercentage += curSubPercentage;

            const currentPathRegion = new Path2D();
            currentPathRegion.moveTo(topLeft.x, topLeft.y);
            currentPathRegion.lineTo(topRight.x, topRight.y);
            currentPathRegion.lineTo(bottomRight.x, bottomRight.y);
            currentPathRegion.lineTo(bottomLeft.x, bottomLeft.y);
            currentPathRegion.lineTo(topLeft.x, topRight.y);
            currentPathRegion.closePath();

            this._internal.context.fillStyle = this._getFillStyleForDrawObject(curFillStyleDrawObject, !!hover);
            this._internal.context.fill(currentPathRegion, 'evenodd');

            lastTopRight = topRight
            lastBottomRight = bottomRight
            curElementIndex++;
        }
    }

    _checkDrawObjectTriangles(currentDrawObject, sizes, hover) {
        const triangleSize = 10;

        if(currentDrawObject.subs && currentDrawObject.subs.reduce((out, {realFillPercentage}) => out+realFillPercentage, 0) > 100) {
            this._drawTriangle({
                x: sizes.pos_x,
                y: sizes.pos_y
            }, triangleSize, 'upper-left', '#ff0000')
        }

        if(currentDrawObject.subs && currentDrawObject.subs.length > 1) {
            const triangleStart = {
                x: sizes.pos_x+sizes.size_x,
                y: sizes.pos_y
            };
            const gradient = this._internal.context.createLinearGradient(triangleStart.x-triangleSize, triangleStart.y, triangleStart.x, triangleStart.y+triangleSize);

            // Add color stops for lines
            gradient.addColorStop(0, "#000000");
            gradient.addColorStop(0.1, "#ffffff");
            gradient.addColorStop(0.2, "#000000");
            gradient.addColorStop(0.3, "#ffffff");
            gradient.addColorStop(0.4, "#000000");
            gradient.addColorStop(0.5, "#ffffff");
            gradient.addColorStop(0.6, "#000000");
            gradient.addColorStop(0.7, "#ffffff");
            gradient.addColorStop(0.8, "#000000");
            gradient.addColorStop(0.9, "#ffffff");
            gradient.addColorStop(1, "#000000");

            this._drawTriangle(triangleStart, triangleSize, 'upper-right', gradient)
        }

        if(currentDrawObject.subs && currentDrawObject.subs.filter(sub => sub.customTime).length > 0) {
            this._drawTriangle({
                x: sizes.pos_x,
                y: sizes.pos_y+sizes.size_y
            }, triangleSize, 'lower-left', '#1100FF')
        }

        if(currentDrawObject.subs && currentDrawObject.subs.filter(sub => sub.alreadyPaid).length > 0) {
            this._drawTriangle({
                x: sizes.pos_x+sizes.size_x,
                y: sizes.pos_y+sizes.size_y
            }, triangleSize, 'lower-right', '#408c26')
        }
    }

    _drawTriangle(start, size, cornerDirection, fillStyle) {
        const currentTrianglePath = new Path2D();
        currentTrianglePath.moveTo(start.x, start.y);

        switch(cornerDirection) {
            case 'upper-left':
                currentTrianglePath.lineTo(start.x+size, start.y);
                currentTrianglePath.lineTo(start.x, start.y+size);
                break;
            case 'upper-right':
                currentTrianglePath.lineTo(start.x, start.y+size);
                currentTrianglePath.lineTo(start.x-size, start.y);
                break;
            case 'lower-left':
                currentTrianglePath.lineTo(start.x+size, start.y);
                currentTrianglePath.lineTo(start.x, start.y-size);
                break;
            case 'lower-right':
                currentTrianglePath.lineTo(start.x-size, start.y);
                currentTrianglePath.lineTo(start.x, start.y-size);
                break;
        }

        currentTrianglePath.closePath();

        this._internal.context.fillStyle = fillStyle;
        this._internal.context.fill(currentTrianglePath);
    }

    _getFillStyleForDrawObject(drawObject, hover) {
        let baseObject = {};

        if (this._internal.selectedIds.indexOf(drawObject.id) > -1) {
            if (drawObject.background_selected) {
                return drawObject.background_selected;
            }
            baseObject = this._internal.drawInfo.defaults.selected;
        } else if (hover) {
            if (drawObject.background_hover) {
                return drawObject.background_hover;
            }
            baseObject = this._internal.drawInfo.defaults.backgrounds_hover;
        } else {
            if (drawObject.background) {
                return drawObject.background;
            }
            baseObject = this._internal.drawInfo.defaults.backgrounds;
        }

        if (drawObject.blocked) {
            return baseObject.blocked;
        } else if (drawObject.bookable) {
            return baseObject.bookable[drawObject.status];
        } else {
            return baseObject.default;
        }
    }

    _getHoveredDrawElement(parentElem) {
        let drawObjects = parentElem ? (parentElem.objects) : this._internal.drawInfo.objects;
        for (let i = 0; i < drawObjects.length; i++) {
            const drawObjectToCheck = drawObjects[i];
            this._internal.context.beginPath();

            switch (drawObjectToCheck.type) {
                case 'rectangle':
                default:
                    let sizes = this._generateRectangleSizes(drawObjectToCheck);
                    this._internal.context.rect(sizes.pos_x, sizes.pos_y, sizes.size_x, sizes.size_y);
                    break;
            }

            const isHovered = this._internal.context.isPointInPath(this._internal.mouse.x, this._internal.mouse.y);
            this._internal.context.closePath();

            if (isHovered) {
                if (drawObjectToCheck.objects && drawObjectToCheck.objects.length > 0) {
                    const hoveredChild = this._getHoveredDrawElement(drawObjectToCheck);
                    if (hoveredChild) {
                        return hoveredChild;
                    }
                }

                return drawObjectToCheck;
            }
        }

        return null;
    }

    _toggleSelectionStatus(drawElement) {
        if (this._internal.selectedIds.indexOf(drawElement.id) > -1) {
            this._internal.selectedIds.splice(this._internal.selectedIds.indexOf(drawElement.id), 1);
            this._events.selectionChange(drawElement, false);
            if (this._internal.selectedIds.length === 0) {
                this._internal.selectedStatus = '';
            }
            this._redrawWholeGraphic();
        } else {
            if (!this._filters.selectable(drawElement)) {
                this._events.unSelectable(drawElement);
                return;
            }
            if (drawElement.bookable) {
                this._internal.selectedStatus = drawElement.status;
            }
            this._internal.selectedIds.push(drawElement.id);
            this._events.selectionChange(drawElement, true);
            this._redrawWholeGraphic();
        }
    };

    _findElementByID(idToSearch, objectsList) {
        for (let i = 0; i < objectsList.length; i++) {
            const currentElem = objectsList[i];
            if (currentElem.id === idToSearch) {
                return currentElem;
            }

            if (currentElem.objects && currentElem.objects.length > 0) {
                const childResult = this._findElementByID(idToSearch, currentElem.objects);
                if (childResult !== null) {
                    return childResult;
                }
            }
        }

        return null;
    }

    // Event Logic
    _defineInternalEvents() {
        let clicks = 0,
            timer,
            dblClickDelay = 250,
            lastClickedElement = null,
            lastEvent = null;

        this._events = {
            nothing: (e) => {
                e.preventDefault();
            },
            mousemove: (e) => {
                this._internal.mouse.x = e.clientX - this.canvas.getBoundingClientRect().left;
                this._internal.mouse.y = e.clientY - this.canvas.getBoundingClientRect().top;

                const curHoverElement = this._getHoveredDrawElement();
                if (this._internal.lastHovered && curHoverElement) {
                    if (this._internal.lastHovered.id === curHoverElement.id) {
                        return true;
                    }
                }

                if (this._internal.events.hoverChange
                    && this._internal.lastHovered !== curHoverElement) {
                    this._internal.events.hoverChange(curHoverElement);
                }

                this._internal.lastHovered = curHoverElement;
                this.redraw();
            },
            clickDecision: (e) => {
                clicks++;  //count clicks
                const clickedElement = this._getHoveredDrawElement();

                if (lastClickedElement && clickedElement && lastClickedElement.id !== clickedElement.id) {
                    clicks = 1;
                }

                lastClickedElement = clickedElement;
                lastEvent = e;

                if (clicks === 1) {
                    timer = setTimeout(() => {
                        this._events.click(e, clickedElement);
                        clicks = 0;
                    }, dblClickDelay);
                } else {
                    clearTimeout(timer);
                    this._events.dblClick(e, clickedElement);
                    clicks = 0;
                }
            },
            click: (e, clickedElement) => {
                e.preventDefault();

                if (clickedElement && clickedElement.clickable) {
                    if (clickedElement.selectable) {
                        this._toggleSelectionStatus(clickedElement);
                    } else {
                        if (this._internal.selectedIds.length > 0) {
                            this._events.unSelectable(clickedElement);
                            return;
                        }

                        if (this._internal.events.click) {
                            this._internal.events.click(e, clickedElement);
                        }
                    }
                }
            },
            dblClick: (e, clickedElement) => {
                e.preventDefault();
                if (clickedElement && clickedElement.clickable && this._internal.events.dblClick) {
                    this._internal.events.dblClick(e, clickedElement);
                }
            },
            selectionChange: (drawElement, selected) => {
                if (drawElement && this._internal.events.selectionChange) {
                    this._internal.events.selectionChange(drawElement, selected, this.getSelected());
                }
            },
            unSelectable: (drawElement) => {
                if (drawElement && this._internal.events.unSelectable) {
                    this._internal.events.unSelectable(drawElement);
                }
            }
        }
    }

    _defineInternalFilters() {
        this._filters = {
            selectable: (drawObject) => {
                let selectable = false;

                if (!drawObject.blocked && drawObject.bookable && ((this._internal.selectedStatus === drawObject.status || this._internal.selectedStatus === '') || this.enableStatusDifferentSelect)) {
                    selectable = true;
                }

                return selectable;
            }
        }
    }

    // Public functions
    redraw() {
        this._redrawWholeGraphic();
    };

    getSelected() {
        let returnObjects = [];
        for (let i = 0; i < this._internal.selectedIds.length; i++) {
            const curIdToSearchFor = this._internal.selectedIds[i],
                fullObject = this._findElementByID(curIdToSearchFor, this._internal.drawInfo.objects);

            if (fullObject) {
                returnObjects.push(fullObject);
            }
        }

        return returnObjects;
    }

    updateDrawInfo(drawInfo) {
        this._internal.drawInfo = drawInfo;
        this._redrawWholeGraphic();
    };

    resetSelected() {
        this._internal.selectedStatus = '';
        this._internal.selectedIds = [];
    }

    setSelected(ids, status) {
        this._internal.selectedStatus = status || 'unbooked';
        this._internal.selectedIds = ids;
        this._redrawWholeGraphic();
    }

    destruct() {
        this._internal.canvas.removeEventListener('mousemove', this._events.mousemove);
        this._internal.canvas.removeEventListener('click', this._events.clickDecision);
        this._internal.canvas.removeEventListener('dblclick', this._events.nothing);
    }
}



// WEBPACK FOOTER //
// ./src/app/pages/seatplans/scripts/drawing.seatplans.festzelt-os.js